import { useCallback, useEffect, useRef, useState } from 'react';
import { logInfo, logWarn } from '@/lib/logger';
import {
  type BeforeInstallPromptChoice,
  type BeforeInstallPromptEvent,
  buildPwaInstallState,
  detectStandaloneMode,
  type PwaInstallState,
} from '@/lib/pwa';

export type PromptInstallResult = BeforeInstallPromptChoice | 'unavailable';

type UsePwaInstallStateResult = {
  installState: PwaInstallState;
  promptInstall: () => Promise<PromptInstallResult>;
};

const resolveMediaQueryMatch = (): boolean => {
  if (typeof window.matchMedia !== 'function') {
    return false;
  }

  const displayModeQuery = window.matchMedia('(display-mode: standalone)');
  return displayModeQuery.matches;
};

const resolveInstallState = (
  promptInstallEvent: BeforeInstallPromptEvent | null
): PwaInstallState => {
  const userAgent = navigator.userAgent;
  const hasPromptInstallEvent = promptInstallEvent !== null;
  const hasServiceWorkerSupport = 'serviceWorker' in navigator;
  const mediaQueryMatches = resolveMediaQueryMatch();
  const isStandalone = detectStandaloneMode(mediaQueryMatches, navigator);

  return buildPwaInstallState({
    userAgent,
    hasPromptInstallEvent,
    hasServiceWorkerSupport,
    isStandalone,
  });
};

export const usePwaInstallState = (): UsePwaInstallStateResult => {
  const promptInstallEventRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [installState, setInstallState] = useState<PwaInstallState>(() =>
    resolveInstallState(null)
  );

  const refreshInstallState = useCallback(() => {
    const nextInstallState = resolveInstallState(promptInstallEventRef.current);
    setInstallState(nextInstallState);
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      const promptInstallEvent = event as BeforeInstallPromptEvent;
      promptInstallEvent.preventDefault();
      promptInstallEventRef.current = promptInstallEvent;
      refreshInstallState();
      logInfo('pwa.install_prompt.available', 'PWA install prompt became available');
    };

    const handleAppInstalled = () => {
      promptInstallEventRef.current = null;
      refreshInstallState();
      logInfo('pwa.app.installed', 'PWA install completed');
    };

    const handleDisplayModeChange = () => {
      refreshInstallState();
    };

    const hasMatchMedia = typeof window.matchMedia === 'function';
    const displayModeQuery = hasMatchMedia
      ? window.matchMedia('(display-mode: standalone)')
      : null;

    refreshInstallState();
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', handleAppInstalled);
    displayModeQuery?.addEventListener?.('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt as EventListener
      );
      window.removeEventListener('appinstalled', handleAppInstalled);
      displayModeQuery?.removeEventListener?.('change', handleDisplayModeChange);
    };
  }, [refreshInstallState]);

  const promptInstall = useCallback(async (): Promise<PromptInstallResult> => {
    const promptInstallEvent = promptInstallEventRef.current;
    if (!promptInstallEvent) {
      return 'unavailable';
    }

    promptInstallEventRef.current = null;

    try {
      await promptInstallEvent.prompt();
      const promptChoice = await promptInstallEvent.userChoice;
      refreshInstallState();
      logInfo('pwa.install_prompt.completed', 'PWA install prompt completed', {
        outcome: promptChoice.outcome,
      });
      return promptChoice.outcome;
    } catch (error) {
      refreshInstallState();
      logWarn('pwa.install_prompt.failed', 'PWA install prompt failed', {
        error: String(error),
      });
      return 'unavailable';
    }
  }, [refreshInstallState]);

  return {
    installState,
    promptInstall,
  };
};
